/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import type { ProgressBarProps } from './types';

const MIN_VALUE = 0;
const MAX_VALUE = 100;

const containerStyles = css({
	width: '100%',
	height: 6,
	position: 'relative',
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.full'),
	overflow: 'hidden',
});

const barStyles = css({
	display: 'block',
	height: 6,
	position: 'absolute',
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.accent.gray.subtler.hovered'),
	transition: 'width 0.8s',
});

/**
 * This is a progress bar component that was built to match the atlaskit component `@atlaskit/progress-bar`.
 * We couldn't use the atlaskit component due to the colour requirements for this progress bar not being supported,
 * hence, a custom component was required.
 */
export default function ProgressBar({
	ariaLabel,
	testId,
	value = 0,
}: ProgressBarProps): JSX.Element {
	const valueParsed = Math.max(MIN_VALUE, Math.min(value, MAX_VALUE));

	return (
		<div
			css={containerStyles}
			role="progressbar"
			aria-label={ariaLabel}
			aria-valuemin={MIN_VALUE}
			aria-valuenow={valueParsed}
			aria-valuemax={MAX_VALUE}
			data-testid={testId}
		>
			<span
				style={{ width: `${valueParsed}%` }}
				css={barStyles}
				data-testid={`${testId}--progress-value-bar`}
			/>
		</div>
	);
}
